import FavouriteButton from "../components/shared/FavouriteButton";
import React, { useState, useEffect, useRef, useLayoutEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSearch } from "../hooks/useSearch";
import { Hotel } from "../types/search";
import { getHotelDetailsBatch, prefetchInspirationHotels } from "../utils/api";
import { prefetchEditorialCollectionHotels } from "../utils/editorialCollections";
import { HomepageContent, fetchHomepageContent, readCachedHomepageContent } from "../utils/homepageContent";
import { CategoryPage, addCategoryCards, fetchCategories } from '../utils/categoryPages';
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import SearchBarNew from "../components/shared/SearchBarNew";
import ProgressiveImage from "../components/shared/ProgressiveImage";
import PageHeader from "../components/shared/PageHeader";

import TrialWelcomePopup from "../components/shared/TrialWelcomePopup";
import Membership from "../components/shared/Membership";
import QuoteForm from "../components/shared/QuoteForm";
import BannerCTA from "../components/shared/BannerCTA";

declare const $: any;

/**
 * Home hero Slick uses variableWidth + margin between slides. Do not add CSS `gap` on `.slick-track`
 * (Slick's transforms ignore it). Values scale with viewport so narrow phones/tablets stay aligned.
 */
function getHomeHeroSliderLayout(containerWidth: number) {
    const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
    if (vw <= 480) {
        return { gap: 12, slideRatio: 0.82, trackMarginRatio: 0.04, firstSlideWidthMul: 1 };
    }
    if (vw <= 768) {
        return { gap: 16, slideRatio: 0.65, trackMarginRatio: 0.06, firstSlideWidthMul: 1 };
    }
    return { gap: 30, slideRatio: 0.48, trackMarginRatio: 0.06, firstSlideWidthMul: 1 };
}

const Home: React.FC = () => {
    const navigate = useNavigate();
    const { hotels, loading, error, clearError, searchByQuery } = useSearch();
    const [homepageContent, setHomepageContent] = useState<HomepageContent>(readCachedHomepageContent);
    const [categoryPages, setCategoryPages] = useState<CategoryPage[]>([]);
    const interestCategories = useMemo(() => addCategoryCards(homepageContent.cards, categoryPages), [homepageContent.cards, categoryPages]);
    const [searchParams, setSearchParams] = useState({
        location: "",
        priceRange: "all",
        rating: "all",
        sortBy: "recommended",
    });
    const [filteredHotels, setFilteredHotels] = useState<Hotel[]>([]);
    const [detailedHotels, setDetailedHotels] = useState<Hotel[]>([]);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
    const [sliderReady, setSliderReady] = useState(false);
    const sliderInitializedRef = useRef(false);
    const sliderContainerRef = useRef<HTMLDivElement>(null);
    const sliderHotels = homepageContent.slider;
    const loadingSliderHotels = false;
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isNavigating, setIsNavigating] = useState(false);
    const [featuredHeartLeft, setFeaturedHeartLeft] = useState<number | null>(null);
    const placeFeaturedHeart = useCallback(() => {
        const gallery = sliderContainerRef.current;
        const slide = gallery?.querySelector('.slick-current');
        if (!gallery || !slide) return;
        const image = slide.getBoundingClientRect();
        const container = gallery.getBoundingClientRect();
        setFeaturedHeartLeft(Math.max(14, Math.min(image.right, container.right) - container.left - 58));
    }, []);
    useLayoutEffect(() => { if (sliderReady) placeFeaturedHeart(); }, [sliderReady, currentSlide, placeFeaturedHeart]);
    const activeSlide = sliderHotels[currentSlide];
    const activeHotelId = activeSlide?.href.match(/^\/hotel\/(\d+)(?:[/?#]|$)/)?.[1];

    useEffect(() => {
        let cancelled = false;
        void fetchHomepageContent().then((content) => {
            if (!cancelled) setHomepageContent((current) =>
                JSON.stringify(current) === JSON.stringify(content) ? current : content
            );
        }).catch((error) => console.warn('Using cached homepage content:', error));
        void fetchCategories().then((categories) => {
            if (!cancelled) setCategoryPages(categories);
        }).catch((error) => console.warn('Category pages unavailable:', error));
        return () => { cancelled = true; };
    }, []);

    // Warm inspiration searches quietly after the homepage settles. Requests are
    // sequential to avoid competing with visible images and are shared with a
    // click that happens while a prefetch is still running.
    useEffect(() => {
        let cancelled = false;
        const inspirationIds = interestCategories
            .map((interest) => {
                const match = interest.href?.match(/[?&]inspirationId=(\d+)/);
                return match ? Number(match[1]) : undefined;
            })
            .filter((id): id is number => typeof id === "number");
        const timer = window.setTimeout(() => {
            void (async () => {
                for (const inspirationId of inspirationIds) {
                    if (cancelled) break;
                    await prefetchInspirationHotels(inspirationId, 20);
                }
            })();
        }, 1200);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [interestCategories]);

    // Interest categories filter state
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [selectedLocation, setSelectedLocation] = useState<string>("all");
    const [filteredInterests, setFilteredInterests] = useState(interestCategories);

    // Extract unique categories and locations
    const allCategories = Array.from(new Set(interestCategories.flatMap((interest) => interest.categories))).sort();
    const allLocations = Array.from(new Set(interestCategories.map((interest) => interest.location))).sort();

    // Function to fetch detailed hotel information
    const fetchHotelDetails = async (hotelIds: number[]) => {
        if (hotelIds.length === 0) return;

        setLoadingDetails(true);
        try {
            console.log("Fetching detailed information for hotels:", hotelIds);
            const detailedHotelsData = await getHotelDetailsBatch(hotelIds);
            setDetailedHotels(detailedHotelsData);
            console.log("Successfully fetched detailed hotel information:", detailedHotelsData);
        } catch (error) {
            console.error("Error fetching hotel details:", error);
            // If detailed fetch fails, we'll still show the basic hotel information
            // Clear any partial data to avoid confusion
            setDetailedHotels([]);
        } finally {
            setLoadingDetails(false);
        }
    };

    // Apply client-side filtering and sorting to API results
    useEffect(() => {
        let filtered = hotels;

        // Apply price filter
        if (searchParams.priceRange !== "all") {
            filtered = filtered.filter((hotel) => {
                const price = hotel.price || 0;
                switch (searchParams.priceRange) {
                    case "low":
                        return price < 200;
                    case "medium":
                        return price >= 200 && price < 300;
                    case "high":
                        return price >= 300;
                    default:
                        return true;
                }
            });
        }

        // Apply rating filter
        if (searchParams.rating !== "all") {
            const minRating = parseInt(searchParams.rating);
            filtered = filtered.filter((hotel) => (hotel.rating || 0) >= minRating);
        }

        // Apply sorting
        switch (searchParams.sortBy) {
            case "price-low":
                filtered = [...filtered].sort((a, b) => (a.price || 0) - (b.price || 0));
                break;
            case "price-high":
                filtered = [...filtered].sort((a, b) => (b.price || 0) - (a.price || 0));
                break;
            case "rating":
                filtered = [...filtered].sort((a, b) => (b.rating || 0) - (a.rating || 0));
                break;
            case "distance":
                filtered = [...filtered].sort((a, b) => {
                    const aDist = parseFloat(a.distance?.split(" ")[0] || "0");
                    const bDist = parseFloat(b.distance?.split(" ")[0] || "0");
                    return aDist - bDist;
                });
                break;
            default:
                break;
        }

        setFilteredHotels(filtered);

        // Fetch detailed information for the filtered hotels
        if (filtered.length > 0) {
            const hotelIds = filtered.map((hotel) => hotel.id);
            fetchHotelDetails(hotelIds);
        } else {
            setDetailedHotels([]);
        }
    }, [hotels, searchParams.priceRange, searchParams.rating, searchParams.sortBy]);

    // Interest category filter handlers
    const toggleCategory = (category: string) => {
        setSelectedCategories((prev) =>
            prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
        );
    };

    const handleLocationChange = (location: string) => {
        setSelectedLocation(location);
    };

    // Filter interests based on selected categories and location
    useEffect(() => {
        let filtered = interestCategories;

        // Filter by location
        if (selectedLocation !== "all") {
            filtered = filtered.filter((interest) => interest.location === selectedLocation);
        }

        // Filter by categories (show if ANY selected category matches)
        if (selectedCategories.length > 0) {
            filtered = filtered.filter((interest) =>
                interest.categories.some((cat) => selectedCategories.includes(cat))
            );
        }

        setFilteredInterests(filtered);
    }, [interestCategories, selectedCategories, selectedLocation]);


    // Cleanup slider before React updates DOM - use useLayoutEffect for synchronous cleanup
    useLayoutEffect(() => {
        return () => {
            // Destroy slider synchronously before React removes the DOM
            if (typeof $ !== "undefined" && $.fn.slick && sliderContainerRef.current) {
                try {
                    const $hotelHeaderGallery = $(sliderContainerRef.current);
                    
                    // Check if element still exists and is initialized
                    if ($hotelHeaderGallery.length > 0 && $hotelHeaderGallery.hasClass("slick-initialized")) {
                        // Remove event listener first
                        $hotelHeaderGallery.off("beforeChange");
                        
                        // Get the slick instance and destroy it properly
                        const slickInstance = $hotelHeaderGallery[0]?.slick;
                        if (slickInstance) {
                            // Destroy slider - wrap in try-catch to handle DOM errors
                            try {
                                $hotelHeaderGallery.slick("unslick");
                            } catch (slickError) {
                                // If unslick fails, try to clean up manually
                                try {
                                    // Remove slick classes and restore original structure
                                    $hotelHeaderGallery.removeClass("slick-initialized slick-slider");
                                    const $slides = $hotelHeaderGallery.find(".slick-slide");
                                    $slides.each(function(this: HTMLElement) {
                                        const $slide = $(this);
                                        const $content = $slide.contents();
                                        if ($content.length > 0) {
                                            $slide.replaceWith($content);
                                        }
                                    });
                                    // Remove slick track and list
                                    $hotelHeaderGallery.find(".slick-track, .slick-list").remove();
                                } catch (cleanupError) {
                                    // Final fallback - just remove classes
                                    console.warn("Slider cleanup failed, removing classes only");
                                }
                            }
                        }
                    }
                } catch (error) {
                    // Silently handle errors during cleanup - element may already be removed
                    // This is expected when React unmounts the component
                } finally {
                    sliderInitializedRef.current = false;
                }
            } else {
                sliderInitializedRef.current = false;
            }
        };
    }, []); // Only cleanup on unmount

    // Slider initialization - reinitialize when hotels are loaded
    useEffect(() => {
        // Don't initialize if hotels are still loading
        if (loadingSliderHotels) {
            return;
        }

        const initSlider = () => {
            if (typeof $ !== "undefined" && $.fn.slick && sliderContainerRef.current) {
                const $hotelHeaderGallery = $(sliderContainerRef.current);
                
                // Destroy existing slider if it exists
                if ($hotelHeaderGallery.hasClass("slick-initialized")) {
                    try {
                        $hotelHeaderGallery.slick("unslick");
                    } catch (error) {
                        console.error("Error destroying existing slider:", error);
                    }
                }
                
                // Check if slider exists and is not already initialized
                if ($hotelHeaderGallery.length > 0 && !$hotelHeaderGallery.hasClass("slick-initialized")) {
                    try {
                        const setTrackMarginOnly = () => {
                            const containerWidth = $hotelHeaderGallery.width() || 0;
                            if (containerWidth <= 0) return;
                            const { trackMarginRatio } = getHomeHeroSliderLayout(containerWidth);
                            const track = $hotelHeaderGallery.find(".slick-track");
                            if (track.length > 0) {
                                track.css({
                                    marginLeft: `${-Math.round(containerWidth * trackMarginRatio)}px`,
                                });
                            }
                            $hotelHeaderGallery.css({ overflow: "hidden" });
                            placeFeaturedHeart();
                        };

                        const applySliderLayout = (runSetPosition: boolean) => {
                            const containerWidth = $hotelHeaderGallery.width() || 0;
                            if (containerWidth <= 0) return;
                            const { gap, slideRatio, trackMarginRatio, firstSlideWidthMul } =
                                getHomeHeroSliderLayout(containerWidth);
                            const availableWidth = containerWidth - gap;
                            const slideWidth = availableWidth * slideRatio;

                            const slickSlides = $hotelHeaderGallery.find(".slick-slide:not(.slick-cloned)");
                            slickSlides.each(function (this: HTMLElement, index: number) {
                                const $slide = $(this);
                                const isFirstSlide = index === 0;
                                const minWidthValue = isFirstSlide ? slideWidth * firstSlideWidthMul : slideWidth;
                                $slide.css({
                                    width: `${slideWidth}px`,
                                    minWidth: `${minWidthValue}px`,
                                    maxWidth: `${slideWidth}px`,
                                    marginRight: `${gap}px`,
                                });
                            });

                            $hotelHeaderGallery.find(".slick-slide.slick-cloned").each(function (this: HTMLElement) {
                                const $slide = $(this);
                                $slide.css({
                                    width: `${slideWidth}px`,
                                    minWidth: `${slideWidth}px`,
                                    maxWidth: `${slideWidth}px`,
                                    marginRight: `${gap}px`,
                                });
                            });

                            const track = $hotelHeaderGallery.find(".slick-track");
                            if (track.length > 0) {
                                track.css({
                                    marginLeft: `${-Math.round(containerWidth * trackMarginRatio)}px`,
                                });
                            }
                            $hotelHeaderGallery.css({ overflow: "hidden" });
                            placeFeaturedHeart();

                            if (runSetPosition) {
                                const slickInstance = $hotelHeaderGallery[0]?.slick;
                                if (slickInstance?.setPosition) {
                                    slickInstance.setPosition();
                                }
                            }
                        };

                        $hotelHeaderGallery.on("init", function () {
                            applySliderLayout(true);
                        });

                        $hotelHeaderGallery.slick({
                            dots: false,
                            infinite: true,
                            slidesToShow: 1,
                            slidesToScroll: 1,
                            autoplay: false,
                            arrows: false,
                            adaptiveHeight: false,
                            centerMode: true,
                            centerPadding: "0",
                            variableWidth: true,
                            speed: 600,
                            cssEase: "ease-in-out",
                        });

                        $hotelHeaderGallery.siblings(".slick-arrow").hide();
                        $hotelHeaderGallery.find(".slick-arrow").hide();

                        setTimeout(() => applySliderLayout(true), 100);
                        setTimeout(() => applySliderLayout(true), 50);

                        let resizeTimer: ReturnType<typeof setTimeout>;
                        $(window).on("resize.sliderWidths", () => {
                            clearTimeout(resizeTimer);
                            resizeTimer = setTimeout(() => applySliderLayout(true), 150);
                        });

                        $hotelHeaderGallery.on("afterChange", function (_event: unknown, _slick: unknown, currentSlideIndex: number) {
                            const totalSlides = sliderHotels.length || 12;
                            const realSlideIndex = currentSlideIndex % totalSlides;
                            setCurrentSlide(realSlideIndex);
                            setTimeout(() => setTrackMarginOnly(), 50);
                        });

                        $hotelHeaderGallery.on("beforeChange", function () {
                            setTrackMarginOnly();
                            setFeaturedHeartLeft(null);
                        });

                        setSliderReady(true);
                        sliderInitializedRef.current = true;
                    } catch (error) {
                        console.error("Error initializing slider:", error);
                        sliderInitializedRef.current = false;
                    }
                }
            } else {
                console.warn("Slick slider library not loaded");
            }
        };

        // The ref is available after this effect runs; initialize on the next paint instead
        // of holding the genuine featured content behind an arbitrary half-second delay.
        const animationFrame = window.requestAnimationFrame(initSlider);

        // Cleanup function to destroy slider when component unmounts or hotels change
        return () => {
            window.cancelAnimationFrame(animationFrame);
            // Remove resize event listener
            if (typeof $ !== "undefined") {
                $(window).off("resize.sliderWidths");
            }
        };
    }, [sliderHotels, loadingSliderHotels, placeFeaturedHeart]);

    const renderStars = (rating: number) => {
        return Array.from({ length: 5 }, (_, i) => (
            <i key={i} className={`star-rating ${i < rating ? "active" : ""}`}></i>
        ));
    };

    return (
        <div className="search-page">
            <Header />
            {/* <PageHeader
                title="Discover Your Dream Destinations"
                text="Browse some of our favourite destinations below. If you don't see the place you have in mind, just get in touch. We work with an extensive collection of handpicked luxury hotels across the globe, in the most extraordinary and sought after locations."
                backgroundImage="/assets/img/slider/1.jpg"
            /> */}
            {/* Search Form */}
            <SearchBarNew />
            <br />
            <div className="container">
                <div className="page-header-content" style={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <h2 style={{ color: "#000" }}>VENTUS' PICKS</h2>
                    <div className="slider-controls" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <button 
                            className="slick-prev-custom" 
                            onClick={() => {
                                if (isNavigating || !sliderContainerRef.current || typeof $ === "undefined") return;
                                setIsNavigating(true);
                                try {
                                    $(sliderContainerRef.current).slick("slickPrev");
                                } catch (error) {
                                    console.error("Error navigating:", error);
                                }
                                setTimeout(() => setIsNavigating(false), 300);
                            }}
                            disabled={isNavigating}
                            style={{
                                background: "none",
                                border: "none",
                                cursor: isNavigating ? "not-allowed" : "pointer",
                                padding: "5px",
                                fontSize: "20px",
                                color: isNavigating ? "#ccc" : "#000",
                                opacity: isNavigating ? 0.5 : 1
                            }}
                        >
                           <svg xmlns="http://www.w3.org/2000/svg" width="5" height="9" viewBox="0 0 5 9" fill="none">
<path d="M4.53027 0.265167L0.530273 4.26517L4.53027 8.26517" stroke="black" stroke-width="0.75"/>
</svg>
                        </button>
                        <span className="slider-counter" style={{ fontSize: "14px", color: "#000", minWidth: "40px", textAlign: "center" }}>
                            {sliderHotels.length > 0 ? `${currentSlide + 1}/${sliderHotels.length}` : ""}
                        </span>
                        <button 
                            className="slick-next-custom" 
                            onClick={() => {
                                if (isNavigating || !sliderContainerRef.current || typeof $ === "undefined") return;
                                setIsNavigating(true);
                                try {
                                    $(sliderContainerRef.current).slick("slickNext");
                                } catch (error) {
                                    console.error("Error navigating:", error);
                                }
                                setTimeout(() => setIsNavigating(false), 300);
                            }}
                            disabled={isNavigating}
                            style={{
                                background: "none",
                                border: "none",
                                cursor: isNavigating ? "not-allowed" : "pointer",
                                padding: "5px",
                                fontSize: "20px",
                                color: isNavigating ? "#ccc" : "#000",
                                opacity: isNavigating ? 0.5 : 1
                            }}
                        >
                           <svg xmlns="http://www.w3.org/2000/svg" width="5" height="9" viewBox="0 0 5 9" fill="none">
<path d="M0.265137 8.26517L4.26514 4.26517L0.265137 0.265167" stroke="black" stroke-width="0.75"/>
</svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Hero Slider Section */}
            <section className="page-header ventus-picks-gallery" style={{  overflow: "hidden", position: "relative" }}>
                {sliderReady && activeHotelId && featuredHeartLeft !== null && <div className="home-pick-favourite" style={{ left: featuredHeartLeft }}>
                    <FavouriteButton key={activeHotelId} hotel={{ id: Number(activeHotelId), name: activeSlide.title, location: activeSlide.subtitle, image: activeSlide.image }} />
                </div>}
                {(!sliderReady || loadingSliderHotels) && (
                    <div style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 10
                    }}>
                        <div className="spinner-border" role="status" style={{ width: "2rem", height: "2rem" }}>
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                )}
                <div
                    ref={sliderContainerRef}
                    className="hotel-header-gallery"
                    style={{ 
                        opacity: sliderReady ? 1 : 0, 
                        transition: "opacity 0.3s ease-in-out", 
                        position: "relative", 
                        overflow: "hidden",
                        width: "100%",
                        height: "100%"
                    }}
                >
                    {loadingSliderHotels ? (
                        <div className="hotel-header-gallery-item" style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                            <div className="spinner-border text-light" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                        </div>
                    ) : sliderHotels.length > 0 ? (
                        sliderHotels.map((slide, index) => {
                            return (
                                <a
                                    key={slide.id || index}
                                    href={slide.href}
                                    onClick={(event) => {
                                        if (slide.href.startsWith('/')) {
                                            event.preventDefault();
                                            navigate(slide.href);
                                        }
                                    }}
                                    className="hotel-header-gallery-item"
                                    style={{ 
                                        width: "100%", 
                                        height: "100%", 
                                        overflow: "hidden",
                                        display: "block",
                                        textDecoration: "none",
                                        cursor: "pointer"
                                    }}
                                >
                                    <ProgressiveImage
                                        src={slide.image}
                                        alt={slide.title || `Hotel ${index + 1}`}
                                        priority={index === 0}
                                    />
                                    <div className="slider-overlay">
                                        <div className="slider-content">
                                            <h2>{slide.title || "Luxury Hotel"}</h2>
                                            <p>{slide.subtitle || "Premium Destination"}</p>
                                        </div>
                                    </div>
                                </a>
                            );
                        })
                    ) : (
                        <div className="hotel-header-gallery-item" style={{ width: "100%", height: "100%" }}>
                            <ProgressiveImage alt="Featured hotels" priority />
                        </div>
                    )}
                </div>
            </section>

            {/* This legacy inline-results block should only appear after an on-page search. */}
            {(loading || Boolean(error) || filteredHotels.length > 0) && (
                <section className={`results-section  ${filteredHotels.length > 0 ? "has-results" : ""}`}>
                <div className="container">
                    <div className="row">
                        {/* Filters Sidebar */}
                        {/* <div className="col-md-3">
              <div className="filters-sidebar">
                <h4>Filters</h4>
                
                <div className="filter-group">
                  <h5>Price Range</h5>
                  <select
                    className="form-control"
                    value={searchParams.priceRange}
                    onChange={(e) => handleInputChange('priceRange', e.target.value)}
                  >
                    <option value="all">All Prices</option>
                    <option value="low">Under $200</option>
                    <option value="medium">$200 - $300</option>
                    <option value="high">Over $300</option>
                  </select>
                </div>

                <div className="filter-group">
                  <h5>Rating</h5>
                  <select
                    className="form-control"
                    value={searchParams.rating}
                    onChange={(e) => handleInputChange('rating', e.target.value)}
                  >
                    <option value="all">All Ratings</option>
                    <option value="5">5 Stars</option>
                    <option value="4">4+ Stars</option>
                    <option value="3">3+ Stars</option>
                  </select>
                </div>

                <div className="filter-group">
                  <h5>Sort By</h5>
                  <select
                    className="form-control"
                    value={searchParams.sortBy}
                    onChange={(e) => handleInputChange('sortBy', e.target.value)}
                  >
                    <option value="recommended">Recommended</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="rating">Highest Rated</option>
                    <option value="distance">Distance</option>
                  </select>
                </div>

                <div className="filter-group">
                  <h5>View Mode</h5>
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setViewMode('grid')}
                    >
                      <i className="fa fa-th"></i> Grid
                    </button>
                    <button
                      type="button"
                      className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setViewMode('list')}
                    >
                      <i className="fa fa-list"></i> List
                    </button>
                  </div>
                </div>
              </div>
            </div> */}

                        {/* Results */}
                        <div className="col-md-12">
                            <div className="results-header">
                                <h3>Found {filteredHotels.length} hotels</h3>
                                {searchParams.location && (
                                    <p>
                                        Searching for hotels in: <strong>{searchParams.location}</strong>
                                    </p>
                                )}
                            </div>
                            {loadingDetails && (
                                <div className="alert alert-info alert-loading " role="alert">
                                    <div className="spinner-border" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>{" "}
                                    Loading detailed hotel information...
                                </div>
                            )}
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    <strong>Search Error:</strong> {error}
                                    <button
                                        type="button"
                                        className="btn-close float-end"
                                        onClick={clearError}
                                        aria-label="Close"
                                    ></button>
                                </div>
                            )}

                            {loading ? (
                                <div className="text-center">
                                    <div className="spinner-border" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                    <p className="mt-2">Searching for hotels...</p>
                                </div>
                            ) : (
                                <div className={`hotels-container ${viewMode === "grid" ? "row" : ""}`}>
                                    {filteredHotels.map((hotel) => {
                                        // Use detailed hotel information if available, otherwise fall back to basic info
                                        const detailedHotel = detailedHotels.find((dh) => dh.id === hotel.id);
                                        const displayHotel = detailedHotel || hotel;

                                        return (
                                            <div
                                                key={hotel.id}
                                                className={viewMode === "grid" ? "col-md-4 mb-4" : "mb-5"}
                                            >
                                                <div className="card hotel-card">
                                                   {/* <div className="card-overlay">Find out more</div> */} 
                                                    <div className="card-image favourite-image">
                                                        <Link to={`/hotel/${hotel.id}`}>
                                                        <ProgressiveImage
                                                            src={displayHotel.images?.[0]?.url || displayHotel.image}
                                                            alt={displayHotel.name}
                                                            style={{ minHeight: "262px" }}
                                                        />
                                                        </Link>
                                                        <FavouriteButton hotel={displayHotel} />
                                                        {loadingDetails && !detailedHotel && (
                                                            <div className="loading-overlay">
                                                                <div
                                                                    className="spinner-border spinner-border-sm"
                                                                    role="status"
                                                                >
                                                                    <span className="sr-only">Loading details...</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="card-content">
                                                        <h4><Link to={`/hotel/${hotel.id}`}>{displayHotel.name}</Link></h4>
                                                        <div className="card-description">
                                                        <Link to={`/hotel/${hotel.id}`}>View hotel <svg xmlns="http://www.w3.org/2000/svg" width="5" height="9" viewBox="0 0 5 9" fill="none">
<path d="M0.275377 8.58105L4.42822 4.42821L0.275378 0.275363" stroke="white" stroke-width="0.778659"/>
</svg></Link>
                                                            {/* 
                                                            {displayHotel.description
                                                                ? displayHotel.description.length > 150
                                                                    ? `${displayHotel.description.substring(0, 150)}...`
                                                                    : displayHotel.description
                                                                : "No description available"}

                                                                */}
                                                        </div>
                                                        {/* 
                                                        <div className="card-info has-border">
                                                            <ul className="card-amenities">
                                                                {(displayHotel.amenities || [])
                                                                    .slice(0, 3)
                                                                    .map((amenity, index) => (
                                                                        <li key={index} className="amenity-tag">
                                                                            {amenity}
                                                                        </li>
                                                                    ))}
                                                                {(displayHotel.amenities || []).length > 3 && (
                                                                    <li className="amenity-tag">
                                                                        +{(displayHotel.amenities || []).length - 3}
                                                                        &nbsp; more
                                                                    </li>
                                                                )}
                                                                {(!displayHotel.amenities ||
                                                                    displayHotel.amenities.length === 0) && (
                                                                    <li className="amenity-tag">
                                                                        Amenities not available
                                                                    </li>
                                                                )}
                                                            </ul>
                                                        </div>
                                                        <div className="card-actions">
                                                            <div className="card-price">
                                                                <span className="price-label">from</span>
                                                                <span className="price-amount">
                                                                    ${displayHotel.price}/night
                                                                </span>
                                                            </div>
                                                        </div>
                                                        */}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {!loading && filteredHotels.length === 0 && (
                                <div className="text-center">
                                    <h4>No hotels found</h4>
                                    <p>Try adjusting your search criteria</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                </section>
            )}

            <section id="destinations" className="interests-section">
                <div className="container">
                    {/* Filters */}
                    <div className="interests-filters">
                        <div className="filter-row">
                            {/* <div className="filter-group">
                                <label className="filter-label">Filter by Interest:</label>
                                <div className="category-filters">
                                    {allCategories.map((category) => (
                                        <button
                                            key={category}
                                            className={`category-filter-btn ${
                                                selectedCategories.includes(category) ? "active" : ""
                                            }`}
                                            onClick={() => toggleCategory(category)}
                                        >
                                            {category}
                                        </button>
                                    ))}
                                </div>
                            </div> */}
                            {/* <div className="filter-group">
                                <label className="filter-label">Location:</label>
                                <select
                                    className="location-filter-dropdown form-select"
                                    value={selectedLocation}
                                    onChange={(e) => handleLocationChange(e.target.value)}
                                >
                                    <option value="all">All Locations</option>
                                    {allLocations.map((location) => (
                                        <option key={location} value={location}>
                                            {location}
                                        </option>
                                    ))}
                                </select>
                            </div> */}
                        </div>
                        {(selectedCategories.length > 0 || selectedLocation !== "all") && (
                            <div className="filter-summary">
                                Showing {filteredInterests.length} of {interestCategories.length} interests
                                <button
                                    className="clear-filters-btn"
                                    onClick={() => {
                                        setSelectedCategories([]);
                                        setSelectedLocation("all");
                                    }}
                                >
                                    Clear All Filters
                                </button>
                            </div>
                        )}
                    </div>


                    
                    <div className="col-md-12">
                        <div className="results-header">
                            <h3>INSPIRATION</h3>
                        </div>
                    </div>
                    {/* Interest Cards */}
                    <div className="row">
                        {filteredInterests.map((interest) => {
                            const urlParams = new URLSearchParams();
                            let searchPath: string | null = null;

                            if (interest.inspirationId) {
                                urlParams.set("inspirationId", interest.inspirationId.toString());
                                urlParams.set("title", interest.title);
                                searchPath = `/search-results?${urlParams.toString()}`;
                            } else if (interest.query) {
                                urlParams.set("location", interest.query);
                                searchPath = `/search-results?${urlParams.toString()}`;
                            }

                            const destination = interest.href || interest.externalUrl || searchPath;
                            const externalDestination = Boolean(destination?.startsWith('https://'));

                            const handleInterestClick = () => {
                                if (externalDestination && destination) {
                                    window.location.assign(destination);
                                    return;
                                }

                                if (destination) {
                                    navigate(destination);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                }
                            };

                            const handleInterestPrefetch = () => {
                                const match = destination?.match(/[?&]inspirationId=(\d+)/);
                                if (match) void prefetchInspirationHotels(Number(match[1]), 20);
                                const collectionMatch = destination?.match(/[?&]collection=([a-z0-9-]+)/);
                                if (collectionMatch) prefetchEditorialCollectionHotels(collectionMatch[1]);
                            };

                            return (
                                <div key={interest.id} className="col-md-4 mb-4">
                                    <div
                                        className="card interest-card"
                                        onMouseEnter={handleInterestPrefetch}
                                        onFocus={handleInterestPrefetch}
                                        onTouchStart={handleInterestPrefetch}
                                    >
                                    {/* 
                                        <div className="card-overlay">Find out more</div>
                                        <div className="card-categories">
                                            {interest.categories.map((category, index) => (
                                                <span key={index} className="category-tag">
                                                    {category}
                                                </span>
                                            ))}
                                        </div>
                                        */}
                                        <div 
                                            className="card-image"
                                            onClick={handleInterestClick}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <ProgressiveImage
                                                src={interest.image}
                                                alt={interest.title}
                                                style={{ minHeight: "262px" }}
                                            />
                                        </div>
                                        <div className="card-content">
                                            <h4 
                                                onClick={handleInterestClick}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {interest.title}
                                            </h4>
                                            <div className="card-description">{interest.description}</div>
                                            {externalDestination && destination ? (
                                                <a href={destination}>
                                                    {interest.ctaLabel || "Find Out More"} <svg xmlns="http://www.w3.org/2000/svg" width="5" height="9" viewBox="0 0 5 9" fill="none">
                                                        <path d="M0.275377 8.58105L4.42822 4.42821L0.275378 0.275363" stroke="white" strokeWidth="0.778659"/>
                                                    </svg>
                                                </a>
                                            ) : destination ? (
                                                <Link to={destination} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                                                    {interest.ctaLabel || "View Hotels"} <svg xmlns="http://www.w3.org/2000/svg" width="5" height="9" viewBox="0 0 5 9" fill="none">
                                                        <path d="M0.275377 8.58105L4.42822 4.42821L0.275378 0.275363" stroke="white" strokeWidth="0.778659"/>
                                                    </svg>
                                                </Link>
                                            ) : null}
                                        </div>
                                        
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* No results message */}
                    {filteredInterests.length === 0 && (
                        <div className="text-center no-results">
                            <h4>No interests match your filters</h4>
                            <p>Try adjusting your filter criteria</p>
                            <button
                                className="btn btn-primary"
                                onClick={() => {
                                    setSelectedCategories([]);
                                    setSelectedLocation("all");
                                }}
                            >
                                Clear All Filters
                            </button>
                        </div>
                    )}
                </div>
            </section>
            <Membership />
            <QuoteForm />
            <BannerCTA />
            <Footer />
            <TrialWelcomePopup />
        </div>
    );
};

export default Home;
